import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import assign from 'lodash/assign';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

// Actions
import { fetchAutocompleteData, goToAddressItem } from '../../flux/actions/AddressActions';
import UserActions from '../../flux/actions/UserActions';

// Misc / Types
import AutocompleteAddress from '../../models/AutocompleteAddress';
import errorConstants from '../../constants/ErrorConstants';
import validator from '../../utils/validator';
import { augmentError } from '../../utils/errors';
import { logError } from '@zg-rentals/log-error';

type ErrorType = {
  nonCriticalError: boolean;
  errorCode: string;
  errorMsg: string;
};

export const useSuggestions = (
  recentSearches: Array<AutocompleteAddress>,
): {
  suggestions: Array<AutocompleteAddress>;
  getSuggestions: (query: string) => void;
} => {
  const dispatch = useDispatch();
  const initState = isEmpty(recentSearches) ? [] : recentSearches;
  const [suggestions, updateSuggestions] = useState(initState);

  const getSuggestions = useCallback(
    debounce((query: string) => {
      // address api requires 7 characters
      if (query.length >= 7) {
        // @ts-ignore
        dispatch(fetchAutocompleteData(query))
          // @ts-expect-error
          .then((res: unknown) => {
            // @ts-expect-error
            updateSuggestions(res);

            return;
          })
          .catch((err: unknown) => {
            logError({
              error: err,
              errorType: 'Suggestions',
              context: {
                details: 'Error fetching autocomplete suggestions',
                root: 'modules/SearchModule/hooks#useSuggestions.getSuggestions',
              },
            });
          });
      } else {
        updateSuggestions(initState);
      }
    }, 200),
    [dispatch, updateSuggestions],
  );

  return { suggestions, getSuggestions };
};

export const useEmail = (): {
  email: string;
  updateEmail: (query: string) => void;
  validateEmail: (emailStr: string) => boolean;
  emailError: string;
} => {
  const dispatch = useDispatch();
  const [email, updateEmail] = useState('');
  const [emailError, updateEmailError] = useState('');

  const validateEmail = (emailStr: string) => {
    // TODO: check if email is associated with current ZG account
    const validatorObj = validator.email(emailStr);
    if (validatorObj.isValid) {
      updateEmailError('');
      dispatch(UserActions.updateUserInfo({ email: emailStr }));
    } else {
      updateEmailError((validatorObj as { isValid: boolean; message: string }).message);
    }

    return validatorObj.isValid;
  };

  return { email, updateEmail, validateEmail, emailError };
};

export const useGoToAddressItem = () => {
  const dispatch = useDispatch();
  const [addressError, updateAddressError] = useState('');
  const [isNoZestModalOpen, openNoZestModal] = useState(false);

  const validateAndGoToAddressItem = ({
    addressInputValue,
    isLoggedIn,
    email,
    setIsLoading,
    unit = '',
    validAddressItem,
    validateEmail,
  }: {
    addressInputValue: string;
    isLoggedIn: boolean;
    email: string;
    setIsLoading: Dispatch<SetStateAction<boolean>>;
    unit?: string;
    validAddressItem: AutocompleteAddress | null;
    validateEmail: (emailStr: string) => boolean;
  }) => {
    /**
     * validate address
     * - validAddressItem was selected from the autocomplete dropdown
     * - addressInputValue has not been changed after selecting address item
     */
    if (!validAddressItem || addressInputValue !== validAddressItem.name) {
      const errMsg = 'Invalid street address. Please select an item from the dropdown.';
      updateAddressError(errMsg);
      return Promise.reject(new Error(errMsg));
    }

    // validate email after validating address so that only one error exists at a time
    const hasValidEmail = isLoggedIn || validateEmail(email);
    if (!hasValidEmail) {
      const errMsg = `Invalid email address entered: ${email}`;
      return Promise.reject(new Error(errMsg));
    }

    setIsLoading(true);
    updateAddressError('');

    const addressItem = validAddressItem && new AutocompleteAddress(validAddressItem);

    const augmentedAddressItem = assign(addressItem, {
      unit,
      isRecent: true,
    });
    // @ts-ignore
    return dispatch(goToAddressItem(augmentedAddressItem)).catch((error: ErrorType) => {
      setIsLoading(false);
      if (error.nonCriticalError) {
        updateAddressError(error.errorMsg);
        if (error.errorCode === errorConstants.RENT_ZESTIMATE_NOT_FOUND) {
          openNoZestModal(true);
        }
        return;
      }

      updateAddressError('Sorry. We could not process your request. Please try again.');
      augmentError(error, {
        hook: 'Error in SearchModule#hooks#validateAndGoToAddressItem',
      });

      throw error;
    });
  };

  return { isNoZestModalOpen, openNoZestModal, addressError, updateAddressError, validateAndGoToAddressItem };
};
